import { nanoid } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import Decimal from 'decimal.js'
import { cloneDeep, isEqual } from 'lodash'
import type {
	ReduxCompany,
	ReduxContact,
	ReduxInvoices,
	ReduxProposals,
} from 'src/types/apiType'
import { getBankAccountProfile } from 'src/utils/bankAccountProfiles'
import { makeErrorToast } from 'src/utils/makeToast'

// Configure Decimal.js for 4 decimal place precision
Decimal.set({ precision: 50, rounding: Decimal.ROUND_HALF_UP })

function roundDecimal(value: Decimal.Value, decimalPlaces = 4) {
	return parseFloat(new Decimal(value || 0).toFixed(decimalPlaces))
}

function scaleTaxDetailsWithReconciliation({
	taxDetails,
	percentage,
	targetAmount,
}: {
	taxDetails: any[]
	percentage: Decimal.Value
	targetAmount: number
}) {
	const taxDetailsClone = cloneDeep(taxDetails || [])

	if (taxDetailsClone.length === 0) {
		return {
			taxDetails: taxDetailsClone,
			totalTaxAmount: 0,
		}
	}

	const hasAllTaxableAmounts = taxDetailsClone.every(
		(t: any) => t?.taxableAmount != null
	)
	let totalTaxAmount = new Decimal(0)

	if (!hasAllTaxableAmounts) {
		taxDetailsClone.forEach((t: any) => {
			t.taxAmount = roundDecimal(
				new Decimal(t?.taxAmount || 0).times(percentage)
			)
			if (t?.taxableAmount != null) {
				t.taxableAmount = roundDecimal(
					new Decimal(t.taxableAmount).times(percentage)
				)
			}
			totalTaxAmount = totalTaxAmount.plus(t.taxAmount || 0)
		})

		return {
			taxDetails: taxDetailsClone,
			totalTaxAmount: roundDecimal(totalTaxAmount),
		}
	}

	let previousTaxableAmount = new Decimal(0)
	const targetAmountDecimal = new Decimal(targetAmount || 0)

	taxDetailsClone.forEach((t: any, index: number) => {
		const taxableAmount =
			index === taxDetailsClone.length - 1
				? roundDecimal(targetAmountDecimal.minus(previousTaxableAmount))
				: roundDecimal(
						new Decimal(t?.taxableAmount || 0).times(percentage)
					)

		t.taxableAmount = taxableAmount
		t.taxAmount = roundDecimal(
			new Decimal(taxableAmount).times(t?.taxRate || 0)
		)

		previousTaxableAmount = previousTaxableAmount.plus(taxableAmount || 0)
		totalTaxAmount = totalTaxAmount.plus(t.taxAmount || 0)
	})

	return {
		taxDetails: taxDetailsClone,
		totalTaxAmount: roundDecimal(totalTaxAmount),
	}
}

function createUpdateData({
	getProposals,
	newProposal,
	//   newProposal,
	getContact,
	getCompany,
}: {
	getProposals: ReduxProposals
	newProposal: any
	//   newProposal: Partial<ReduxProposals>,
	getContact: ReduxContact
	getCompany: ReduxCompany
}) {
	const allContacts = getContact.contactDetails || []
	let currentContact
	if (allContacts.length > 0 && newProposal.contactId) {
		currentContact = allContacts.find(
			(contact: any) => contact.id === newProposal.contactId
		)
	}
	let clientName
	if (currentContact) {
		clientName = currentContact?.fullName
		getProposals.clientEmail = currentContact?.email
	}
	const clientAddress = `${getContact.line01}${getContact.line01 && '-'}${
		getContact.line02
	}${getContact.line02 && '- '}${getContact.country}`
	getProposals.clientId = newProposal.contact
	getProposals.contactId = newProposal.contactId
	getProposals.clientName = getContact.name
	getProposals.contactName = clientName
	//   getProposals.clientEmail = getContact.email

	getProposals.clientAddress = clientAddress
	getProposals.clientCountry = getContact.country
	getProposals.clientIdNumber = getContact.idnumber
	getProposals.clientVatNumber = getContact.vatNumber
	getProposals.clientCurrency = getContact.currency
	getProposals.clientTaxRate = getContact.taxRate
	getProposals.clientTaxType = getContact.taxType
	getProposals.clientNote = getContact.note
	getProposals.isDetailed = newProposal.isDetailed
	getProposals.showBidLetter = newProposal.showBidLetter
	getProposals.isClientTaxRate = newProposal.isClientTaxRate
	getProposals.companyLogo = newProposal.companyLogo
	// if (newProposal.isClientTaxRate) {
	//   getProposals.selectedTaxRate = newProposal.contactTaxRate
	// } else {
	//   getProposals.selectedTaxRate = newProposal.quoteTaxRate
	// }
	// getProposals.isClientCurrency = newProposal.isClientCurrency
	// if (newProposal.isClientCurrency) {
	//   getProposals.selectedCurrency = newProposal.contactCurrency
	// } else {
	//   getProposals.selectedCurrency = newProposal.quoteCurrency
	// }
	if (newProposal.bankAccount) {
		const bankProfile = getBankAccountProfile(
			getCompany,
			newProposal.bankAccount
		)
		getProposals.bankProfileNumber = bankProfile.profileId
		if (bankProfile.profileId !== 'empty') {
			getProposals.proposalsBankProfileName = bankProfile.bankProfileName
			getProposals.proposalsBank = bankProfile.bank
			getProposals.proposalsIban = bankProfile.iban
			getProposals.proposalsBic = bankProfile.bic
			getProposals.proposalsAccountName = bankProfile.accountName
		}
	}

	getProposals.noofInvoices = newProposal.invoiceCount

	if (
		getProposals.noofInvoices &&
		Number.isNaN(parseInt(getProposals.noofInvoices as string))
	) {
		makeErrorToast('Something Unexpected Happened With Invoice Count!')
	}

	const updateContact: any = {}

	if (
		currentContact &&
		!isEqual(currentContact?.email, newProposal.contactEmail)
	) {
		const updateCurrent = {
			...currentContact,
			email: newProposal.contactEmail,
		}
		updateContact.contactDetails = [updateCurrent]
	}
	if (!isEqual(getContact.currency, newProposal.contactCurrency)) {
		updateContact.currency = newProposal.contactCurrency
	}
	if (!isEqual(getContact.taxRate, newProposal.contactTaxRate)) {
		updateContact.taxRate = newProposal.contactTaxRate
	}

	return { getProposals, updateContact }
}

function calculateProposalAndInvoiceTotals({
	getProposals,
	newProposal,
	//   newProposal
	getContact,
	quoteTotals,
	getCompany,
}: {
	getProposals: ReduxProposals
	newProposal: any
	//   newProposal: Partial<ReduxProposals>,
	getContact: ReduxContact
	quoteTotals: any
	getCompany: ReduxCompany
}) {
	const totalSectionDetailsQuote = cloneDeep(
		quoteTotals?.subtotalsWithQuoteType
	)
	const totalSectionDetailsAccSection = cloneDeep(
		quoteTotals?.subtotalsWithAccSections
	)

	if (newProposal.isClientTaxRate) {
		return calculateWithClientTaxRate({
			getProposals,
			newProposal,
			//   newProposal
			getContact,
			quoteTotals,
			totalSectionDetailsQuote,
			totalSectionDetailsAccSection,
			getCompany,
		})
	}
	return calculateWithoutClientTaxRate({
		getProposals,
		newProposal,
		//   newProposal
		getContact,
		quoteTotals,
		totalSectionDetailsQuote,
		totalSectionDetailsAccSection,
		getCompany,
	})
}
function calculateCreditInvoiceTotals({
	originalInvoiceData,
	creditInvoiceData,
	copyInvoiceData,
	companyDetails,
	getContact,
	tenantId,
}: {
	originalInvoiceData: ReduxInvoices
	creditInvoiceData: any
	copyInvoiceData: any
	companyDetails: ReduxCompany
	getContact: ReduxContact
	tenantId: string
}) {
	const totalSectionDetailsQuote = cloneDeep(
		originalInvoiceData.sectionDetailsByQuote
	)
	const totalSectionDetailsAccSection = cloneDeep(
		originalInvoiceData.sectionDetailsByAccSection
	)

	if (originalInvoiceData.isClientTaxRate) {
		return calculateCreditInvoiceWithClientTaxRate({
			originalInvoiceData,
			creditInvoiceData,
			copyInvoiceData,
			companyDetails,
			getContact,
			tenantId,
			totalSectionDetailsQuote,
			totalSectionDetailsAccSection,
		})
	}
	return calculateCreditInvoiceWithoutClientTaxRate({
		originalInvoiceData,
		creditInvoiceData,
		copyInvoiceData,
		companyDetails,
		getContact,
		tenantId,
		totalSectionDetailsQuote,
		totalSectionDetailsAccSection,
	})
}
// -----------------------Credit Note-------------------------------
function calculateCreditInvoiceWithoutClientTaxRate({
	originalInvoiceData,
	creditInvoiceData,
	copyInvoiceData,
	companyDetails,
	getContact,
	tenantId,
	totalSectionDetailsQuote,
	totalSectionDetailsAccSection,
}: {
	originalInvoiceData: ReduxInvoices
	creditInvoiceData: any
	copyInvoiceData: any
	companyDetails: ReduxCompany
	getContact: ReduxContact
	tenantId: string
	totalSectionDetailsQuote: any
	totalSectionDetailsAccSection: any
}) {
	const zeroPad = (num: number, places: number) =>
		String(num).padStart(places, '0')

	let newInvoiceNumber = '0'
	let newInvoicingyear = ''
	if (companyDetails.invoiceCount === null) {
		newInvoiceNumber = zeroPad(1, 4)
	} else {
		if (companyDetails.invoiceCount || companyDetails.invoiceCount === 0) {
			newInvoiceNumber = zeroPad(companyDetails?.invoiceCount + 1, 4)
			newInvoicingyear = dayjs().toISOString()
		}
	}
	if (companyDetails.invoicingYear === null) {
		newInvoicingyear = dayjs().toISOString()
	} else {
		if (dayjs().diff(dayjs(companyDetails.invoicingYear), 'year') > 0) {
			newInvoicingyear = dayjs().toISOString()
			newInvoiceNumber = zeroPad(1, 4)
		}
	}
	const year = dayjs(newInvoicingyear).year()

	let invoiceDate

	if (creditInvoiceData?.invoiceDate?.toString() === 'Invalid Date') {
		invoiceDate = dayjs().toISOString()
	} else {
		invoiceDate = dayjs(creditInvoiceData.invoiceDate).toISOString()
	}
	const creditAmount = parseFloat(
		parseFloat(creditInvoiceData?.amount || 0).toFixed(4)
	)
	const scaledTaxDetails = scaleTaxDetailsWithReconciliation({
		taxDetails: originalInvoiceData.taxDetails || [],
		percentage:
			parseFloat(creditInvoiceData.invoiceAmountPercentage || 0) / 100,
		targetAmount: creditAmount,
	})

	const newSectionDetailsByQuote = cloneDeep(totalSectionDetailsQuote)
	if (newSectionDetailsByQuote && newSectionDetailsByQuote.length > 0) {
		newSectionDetailsByQuote.map((d: any) => {
			d.amount = parseFloat(
				(
					(parseFloat(creditInvoiceData.invoiceAmountPercentage) /
						100) *
					d.amount
				).toFixed(4)
			)
			return d
		})
	}

	const newsectionDetailsByAccSection = cloneDeep(
		totalSectionDetailsAccSection
	)
	if (
		newsectionDetailsByAccSection &&
		newsectionDetailsByAccSection.length > 0
	) {
		newsectionDetailsByAccSection.map((d: any) => {
			d.amount = parseFloat(
				(
					(parseFloat(creditInvoiceData.invoiceAmountPercentage) /
						100) *
					d.amount
				).toFixed(4)
			)
			return d
		})
	}
	const taxAmount = scaledTaxDetails.totalTaxAmount
	const amountAfterTax = roundDecimal(
		new Decimal(creditAmount).plus(taxAmount || 0)
	)

	const updateCompanyDetails = {
		id: companyDetails.id,
		invoiceCount: newInvoiceNumber,
		invoicingYear: newInvoicingyear,
	}

	if (!copyInvoiceData?.assignedTenantId) {
		delete copyInvoiceData?.assignedTenantId
	}
	if (!copyInvoiceData?.assignedTenantName) {
		delete copyInvoiceData?.assignedTenantName
	}

	const newInvoice = {
		...copyInvoiceData,
		id: nanoid(),
		tenantId: tenantId,
		activeFlag: true,
		title: `Credit Note ${newInvoiceNumber}`,
		invoiceNumber: `${
			companyDetails?.invoiceNumberPrefix ?? 'INV'
		}-${year}-${newInvoiceNumber}`,
		invoiceAmountPercentage: parseFloat(
			creditInvoiceData.invoiceAmountPercentage
		),
		amount: parseFloat(creditInvoiceData.amount),
		amountAfterTax: amountAfterTax,
		taxAmount: taxAmount,
		taxDetails: scaledTaxDetails.taxDetails,
		invoiceDate: invoiceDate,
		creditComments: creditInvoiceData.comments,
		originalInvoiceId: originalInvoiceData.id,
		sectionDetailsByQuote: newSectionDetailsByQuote,
		sectionDetailsByAccSection: newsectionDetailsByAccSection,
		salesAccNumberType: originalInvoiceData?.salesAccNumberType,
		invoiceType: 'credit',
	}
	delete newInvoice.uniqueTransactionId

	const updateOriginalInvoice = {
		id: originalInvoiceData.id,
		creditInvoiceGenerated: true,
	}
	return { newInvoice, updateOriginalInvoice, updateCompanyDetails }
}
function calculateCreditInvoiceWithClientTaxRate({
	originalInvoiceData,
	creditInvoiceData,
	copyInvoiceData,
	companyDetails,
	getContact,
	tenantId,
	totalSectionDetailsQuote,
	totalSectionDetailsAccSection,
}: {
	originalInvoiceData: ReduxInvoices
	creditInvoiceData: any
	copyInvoiceData: any
	companyDetails: ReduxCompany
	getContact: ReduxContact
	tenantId: string
	totalSectionDetailsQuote: any
	totalSectionDetailsAccSection: any
}) {
	const zeroPad = (num: number, places: number) =>
		String(num).padStart(places, '0')

	let newInvoiceNumber = '0'
	let newInvoicingyear = ''
	if (companyDetails.invoiceCount === null) {
		newInvoiceNumber = zeroPad(1, 4)
	} else {
		if (companyDetails.invoiceCount || companyDetails.invoiceCount === 0) {
			newInvoiceNumber = zeroPad(companyDetails?.invoiceCount + 1, 4)
			newInvoicingyear = dayjs().toISOString()
		}
	}
	if (companyDetails.invoicingYear === null) {
		newInvoicingyear = dayjs().toISOString()
	} else {
		if (dayjs().diff(dayjs(companyDetails.invoicingYear), 'year') > 0) {
			newInvoicingyear = dayjs().toISOString()
			newInvoiceNumber = zeroPad(1, 4)
		}
	}
	const year = dayjs(newInvoicingyear).year()

	let invoiceDate

	if (creditInvoiceData?.invoiceDate?.toString() === 'Invalid Date') {
		invoiceDate = dayjs().toISOString()
	} else {
		invoiceDate = dayjs(creditInvoiceData.invoiceDate).toISOString()
	}

	let tax
	let taxAmount
	let amountAfterTax
	let roundedTaxAmount
	if (creditInvoiceData?.amount) {
		const creditAmount = parseFloat(
			parseFloat(creditInvoiceData?.amount).toFixed(4)
		)
		tax = parseFloat(getContact.taxRate as string)
		taxAmount = (tax / 100) * creditAmount
		roundedTaxAmount = parseFloat(taxAmount.toFixed(4))

		amountAfterTax = creditAmount + roundedTaxAmount
	}
	const taxDetails = {
		taxRate: parseFloat(getContact.taxRate as string) / 100,
		taxPercentage: parseFloat(getContact.taxRate as string),
		taxAmount: roundedTaxAmount,
		taxableAmount: parseFloat(creditInvoiceData?.amount),
	}
	const newSectionDetailsByQuote = cloneDeep(totalSectionDetailsQuote)
	if (newSectionDetailsByQuote && newSectionDetailsByQuote.length > 0) {
		newSectionDetailsByQuote.map((d: any) => {
			d.amount = parseFloat(
				(
					(parseFloat(creditInvoiceData.invoiceAmountPercentage) /
						100) *
					d.amount
				).toFixed(4)
			)
			return d
		})
	}

	const newsectionDetailsByAccSection = cloneDeep(
		totalSectionDetailsAccSection
	)
	if (
		newsectionDetailsByAccSection &&
		newsectionDetailsByAccSection.length > 0
	) {
		newsectionDetailsByAccSection.map((d: any) => {
			d.amount = parseFloat(
				(
					(parseFloat(creditInvoiceData.invoiceAmountPercentage) /
						100) *
					d.amount
				).toFixed(4)
			)
			return d
		})
	}

	const updateCompanyDetails = {
		id: companyDetails.id,
		invoiceCount: newInvoiceNumber,
		invoicingYear: newInvoicingyear,
	}
	if (!copyInvoiceData?.assignedTenantId) {
		delete copyInvoiceData?.assignedTenantId
	}
	if (!copyInvoiceData?.assignedTenantName) {
		delete copyInvoiceData?.assignedTenantName
	}

	const newInvoice = {
		...copyInvoiceData,
		id: nanoid(),
		tenantId: tenantId,
		activeFlag: true,
		title: `Credit Note ${newInvoiceNumber}`,
		invoiceNumber: `${
			companyDetails?.invoiceNumberPrefix ?? 'INV'
		}-${year}-${newInvoiceNumber}`,
		invoiceAmountPercentage: parseFloat(
			creditInvoiceData.invoiceAmountPercentage
		),
		amount: parseFloat(creditInvoiceData.amount),
		amountAfterTax: amountAfterTax || 0,
		taxAmount: taxAmount,
		taxDetails: [taxDetails],
		invoiceDate: invoiceDate,
		creditComments: creditInvoiceData.comments,
		originalInvoiceId: originalInvoiceData.id,
		sectionDetailsByQuote: newSectionDetailsByQuote,
		sectionDetailsByAccSection: newsectionDetailsByAccSection,
		salesAccNumberType: originalInvoiceData?.salesAccNumberType,
		invoiceType: 'credit',
	}
	delete newInvoice.uniqueTransactionId

	const updateOriginalInvoice = {
		id: originalInvoiceData.id,
		creditInvoiceGenerated: true,
	}
	return { newInvoice, updateOriginalInvoice, updateCompanyDetails }
}

// -----------------------Proposals & Invoices-------------------------------
function calculateWithClientTaxRate({
	getProposals,
	newProposal,
	//   newProposal
	getContact,
	quoteTotals,
	totalSectionDetailsQuote,
	totalSectionDetailsAccSection,
	getCompany,
}: {
	getProposals: ReduxProposals
	newProposal: any
	//   newProposal: Partial<ReduxProposals>,
	getContact: ReduxContact
	quoteTotals: any
	totalSectionDetailsQuote: any
	totalSectionDetailsAccSection: any
	getCompany: ReduxCompany
}) {
	for (let i = 0; i < newProposal.drafts.length; i++) {
		if (
			getProposals.status !== 'Generate without PO' &&
			getProposals.status !== 'Generate'
		) {
			newProposal.drafts[i].invoiceId =
				getProposals?.drafts?.[i]?.invoiceId
			newProposal.drafts[i].generated =
				getProposals.drafts !== null
					? getProposals?.drafts?.[i]?.generated
					: false
		}
		const currentDraftAmount = parseFloat(
			parseFloat(newProposal.drafts[i].amount).toFixed(4)
		)
		newProposal.drafts[i].amount = currentDraftAmount
		newProposal.drafts[i].taxAmount =
			(parseFloat(getContact.taxRate as string) / 100) *
			currentDraftAmount
		const currentDraftTaxAmount = parseFloat(
			newProposal.drafts[i].taxAmount.toFixed(4)
		)
		newProposal.drafts[i].taxAmount = currentDraftTaxAmount
		newProposal.drafts[i].amountAfterTax = parseFloat(
			(currentDraftAmount + currentDraftTaxAmount).toFixed(4)
		)

		newProposal.drafts[i].taxDetails = [
			{
				taxRate: parseFloat(getContact.taxRate as string) / 100,
				taxPercentage: parseFloat(getContact.taxRate as string),
				taxAmount: currentDraftTaxAmount,
				taxableAmount: currentDraftAmount,
			},
		]
		const newsectionDetailsByQuote = cloneDeep(totalSectionDetailsQuote)
		if (newsectionDetailsByQuote && newsectionDetailsByQuote.length > 0) {
			newsectionDetailsByQuote.map((d: any) => {
				d.amount = parseFloat(
					(
						(parseFloat(
							newProposal.drafts[i].proposalAmountPercentage
						) /
							100) *
						d.amount
					).toFixed(4)
				)
				return d
			})
		}
		newProposal.drafts[i].sectionDetailsByQuote = newsectionDetailsByQuote

		const newsectionDetailsByAccSection = cloneDeep(
			totalSectionDetailsAccSection
		)
		if (
			newsectionDetailsByAccSection &&
			newsectionDetailsByAccSection.length > 0
		) {
			newsectionDetailsByAccSection.map((d: any) => {
				d.amount = parseFloat(
					(
						(parseFloat(
							newProposal.drafts[i].proposalAmountPercentage
						) /
							100) *
						d.amount
					).toFixed(4)
				)
				return d
			})
		}
		newProposal.drafts[i].sectionDetailsByAccSection =
			newsectionDetailsByAccSection
		newProposal.drafts[i].salesAccNumberType =
			getCompany?.salesAccNumberType

		newProposal.drafts[i].invoiceDate = newProposal?.drafts?.[i]
			?.invoiceDate
			? dayjs(newProposal.drafts[i].invoiceDate).toISOString()
			: null
		newProposal.drafts[i].invoiceDueDate = newProposal?.drafts?.[i]
			?.invoiceDueDate
			? dayjs(newProposal.drafts[i].invoiceDueDate).toISOString()
			: null

		if (i === newProposal.drafts.length - 1) {
			let totalAmount = 0
			let totalPercent = 0
			for (let j = 0; j <= i; j++) {
				totalAmount += parseFloat(
					parseFloat(newProposal.drafts[j].amount ?? 0).toFixed(4)
				)
				totalPercent += parseFloat(
					parseFloat(
						newProposal.drafts[j].proposalAmountPercentage ?? 0
					).toFixed(4)
				)
			}
			newProposal.drafts[i].balancePercentage = parseFloat(
				(100 - totalPercent).toFixed(4)
			)
			newProposal.drafts[i].balanceAmount = parseFloat(
				// @ts-expect-error
				parseFloat(getProposals.total) - totalAmount
			).toFixed(4)
		} else {
			let totalAmount = 0
			let totalPercent = 0

			for (let j = 0; j < newProposal.drafts.length - 1; j++) {
				totalAmount += parseFloat(
					parseFloat(newProposal.drafts[j].amount ?? 0).toFixed(4)
				)

				totalPercent += parseFloat(
					parseFloat(
						newProposal.drafts[j].proposalAmountPercentage ?? 0
					).toFixed(4)
				)
			}

			newProposal.drafts[i].balancePercentage = parseFloat(
				(100 - totalPercent).toFixed(4)
			)
			newProposal.drafts[i].balanceAmount = parseFloat(
				// @ts-expect-error
				parseFloat(getProposals.total) - totalAmount
			).toFixed(4)
		}
	}

	return newProposal
}

function calculateWithoutClientTaxRate({
	getProposals,
	newProposal,
	//   newProposal
	getContact,
	quoteTotals,
	totalSectionDetailsQuote,
	totalSectionDetailsAccSection,
	getCompany,
}: {
	getProposals: ReduxProposals
	newProposal: any
	//   newProposal: Partial<ReduxProposals>,
	getContact: ReduxContact
	quoteTotals: any
	totalSectionDetailsQuote: any
	totalSectionDetailsAccSection: any
	getCompany: ReduxCompany
}) {
	const totalsWithTax = quoteTotals.totalsWithTax
	for (let i = 0; i < newProposal.drafts.length; i++) {
		if (
			getProposals.status !== 'Generate without PO' &&
			getProposals.status !== 'Generate'
		) {
			newProposal.drafts[i].invoiceId =
				getProposals?.drafts?.[i]?.invoiceId
			newProposal.drafts[i].generated =
				getProposals.drafts !== null
					? getProposals?.drafts?.[i]?.generated
					: false
		}
		const currentDraftAmount = parseFloat(
			parseFloat(newProposal.drafts[i].amount).toFixed(4)
		)
		newProposal.drafts[i].amount = currentDraftAmount

		const scaledTaxDetails = scaleTaxDetailsWithReconciliation({
			taxDetails: totalsWithTax.taxDetails || [],
			percentage:
				parseFloat(
					newProposal.drafts[i].proposalAmountPercentage || 0
				) / 100,
			targetAmount: currentDraftAmount,
		})
		const currentDraftTaxAmount = scaledTaxDetails.totalTaxAmount
		newProposal.drafts[i].taxAmount = currentDraftTaxAmount
		newProposal.drafts[i].amountAfterTax = roundDecimal(
			new Decimal(currentDraftAmount).plus(currentDraftTaxAmount)
		)
		newProposal.drafts[i].taxDetails = scaledTaxDetails.taxDetails

		const newsectionDetailsByQuote = cloneDeep(totalSectionDetailsQuote)
		if (newsectionDetailsByQuote && newsectionDetailsByQuote.length > 0) {
			newsectionDetailsByQuote.map((d: any) => {
				d.amount = parseFloat(
					(
						(parseFloat(
							newProposal.drafts[i].proposalAmountPercentage
						) /
							100) *
						d.amount
					).toFixed(4)
				)
				return d
			})
		}

		newProposal.drafts[i].sectionDetailsByQuote = newsectionDetailsByQuote

		const newsectionDetailsByAccSection = cloneDeep(
			totalSectionDetailsAccSection
		)
		if (
			newsectionDetailsByAccSection &&
			newsectionDetailsByAccSection.length > 0
		) {
			newsectionDetailsByAccSection.map((d: any) => {
				d.amount = parseFloat(
					(
						(parseFloat(
							newProposal.drafts[i].proposalAmountPercentage
						) /
							100) *
						d.amount
					).toFixed(4)
				)
				return d
			})
		}

		newProposal.drafts[i].sectionDetailsByAccSection =
			newsectionDetailsByAccSection
		newProposal.drafts[i].salesAccNumberType =
			getCompany?.salesAccNumberType

		newProposal.drafts[i].invoiceDate = newProposal?.drafts?.[i]
			?.invoiceDate
			? dayjs(newProposal.drafts[i].invoiceDate).toISOString()
			: null
		newProposal.drafts[i].invoiceDueDate = newProposal?.drafts?.[i]
			?.invoiceDueDate
			? dayjs(newProposal.drafts[i].invoiceDueDate).toISOString()
			: null

		if (i === newProposal.drafts.length - 1) {
			let totalAmount = 0
			let totalPercent = 0
			for (let j = 0; j <= i; j++) {
				totalAmount += parseFloat(
					parseFloat(newProposal.drafts[j].amount ?? 0).toFixed(4)
				)
				totalPercent += parseFloat(
					parseFloat(
						newProposal.drafts[j].proposalAmountPercentage ?? 0
					).toFixed(4)
				)
			}
			newProposal.drafts[i].balancePercentage = parseFloat(
				(100 - totalPercent).toFixed(4)
			)
			newProposal.drafts[i].balanceAmount = parseFloat(
				// @ts-expect-error
				parseFloat(getProposals.total) - totalAmount
			).toFixed(4)
		} else {
			let totalAmount = 0
			let totalPercent = 0
			for (let j = 0; j < newProposal.drafts.length - 1; j++) {
				totalAmount += parseFloat(
					parseFloat(newProposal.drafts[j].amount ?? 0).toFixed(4)
				)
				totalPercent += parseFloat(
					parseFloat(
						newProposal.drafts[j].proposalAmountPercentage ?? 0
					).toFixed(4)
				)
			}

			newProposal.drafts[i].balancePercentage = parseFloat(
				(100 - totalPercent).toFixed(4)
			)
			newProposal.drafts[i].balanceAmount = parseFloat(
				// @ts-expect-error
				parseFloat(getProposals.total) - totalAmount
			).toFixed(4)
		}
	}

	return newProposal
}

export {
	calculateCreditInvoiceTotals,
	calculateProposalAndInvoiceTotals,
	createUpdateData,
}
